import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, switchMap, take } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import {
  AddCycleCountItemsResponse,
  CompletedCycleCount,
  CycleCountSessionHeader,
  CycleCountSessionItem,
  CycleCountItemHistoryEntry,
  CycleCountVarianceReport,
  CycleCountVarianceReportItem,
  PostAdjustmentsRequest,
  PostAdjustmentsResponse,
  RecordCountRequest,
  SubmitSessionResponse,
} from '../models/cycle-count-session.model';

const BASE_URL = '/api/cycleCounts';

@Injectable({
  providedIn: 'root',
})
export class CycleCountSessionService {
  private http: HttpClient;

  constructor(
    httpClientFactory: MyHttpClientFactory,
    private auth: AuthService
  ) {
    this.http = httpClientFactory.createHttpClient(
      environment.urls.toolTrackerAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  getSessionHeader(cycleCountId: string): Observable<CycleCountSessionHeader> {
    return this.http.get<CycleCountSessionHeader>(
      `${BASE_URL}/${cycleCountId}`
    );
  }

  getItems(cycleCountId: string): Observable<CycleCountSessionItem[]> {
    return this.http.get<CycleCountSessionItem[]>(
      `${BASE_URL}/${cycleCountId}/items`
    );
  }

  /**
   * #30580: reopens a closed (Complete/Adjusted) count back to InProgress so more items can be added.
   * Returns the updated header.
   */
  reopenSession(cycleCountId: string): Observable<CycleCountSessionHeader> {
    return this.http.post<CycleCountSessionHeader>(
      `${BASE_URL}/${cycleCountId}/reopen`,
      {}
    );
  }

  /**
   * #30580: lists the completed (Complete/Adjusted) counts for a warehouse, most-recent first, to
   * populate the detail page's variance-report dropdown.
   */
  getCompletedCounts(warehouseId: number): Observable<CompletedCycleCount[]> {
    return this.http.get<CompletedCycleCount[]>(
      `${BASE_URL}/warehouse/${warehouseId}/completed`
    );
  }

  /**
   * #30580: grows an open count when its size is raised (e.g. 10 → 15). Appends only the delta of new
   * items, excluding items already on the count.
   */
  addItems(
    cycleCountId: string,
    newCountSize: number
  ): Observable<AddCycleCountItemsResponse> {
    return this.http.post<AddCycleCountItemsResponse>(
      `${BASE_URL}/${cycleCountId}/add-items`,
      { newCountSize }
    );
  }

  recordCount(
    cycleCountId: string,
    itemId: string,
    request: RecordCountRequest
  ): Observable<CycleCountSessionItem> {
    // #31393: stamp who counted from the live Auth0 session (display name, falling back to email) so the
    // variance report and count history can show the counter. Done here, where the request is sent, so the
    // counting screen (and the offline-queue replay that reuses it) needs no change. A request that already
    // carries countedBy (e.g. a future caller) keeps its value.
    return this.auth.user$.pipe(
      take(1),
      switchMap((user) => {
        const countedBy =
          request.countedBy ??
          ((user?.name ?? user?.email ?? '').trim() || null);
        return this.http.put<CycleCountSessionItem>(
          `${BASE_URL}/${cycleCountId}/items/${itemId}`,
          { ...request, countedBy }
        );
      })
    );
  }

  /**
   * #31334: deletes one item (line) from an open cycle count. Admin-only server-side (EditAll on the
   * cycle-count admin feature); a non-admin request is rejected with 403. Returns the refreshed session
   * header so the caller can update the count size / totals.
   */
  deleteItem(
    cycleCountId: string,
    itemId: string
  ): Observable<CycleCountSessionHeader> {
    return this.http.delete<CycleCountSessionHeader>(
      `${BASE_URL}/${cycleCountId}/items/${itemId}`
    );
  }

  submitSession(cycleCountId: string): Observable<SubmitSessionResponse> {
    return this.http.post<SubmitSessionResponse>(
      `${BASE_URL}/${cycleCountId}/items/submit`,
      {}
    );
  }

  getVarianceReport(
    cycleCountId: string
  ): Observable<CycleCountVarianceReport> {
    return this.http.get<CycleCountVarianceReport>(
      `${BASE_URL}/${cycleCountId}/items/variance-report`
    );
  }

  /** #31393: an item's count history — each count event with who counted it, most-recent first. */
  getCountHistory(
    cycleCountId: string,
    itemId: string
  ): Observable<CycleCountItemHistoryEntry[]> {
    return this.http.get<CycleCountItemHistoryEntry[]>(
      `${BASE_URL}/${cycleCountId}/items/${itemId}/count-history`
    );
  }

  postAdjustments(
    cycleCountId: string,
    request: PostAdjustmentsRequest
  ): Observable<PostAdjustmentsResponse> {
    return this.http.post<PostAdjustmentsResponse>(
      `${BASE_URL}/${cycleCountId}/adjustments`,
      request
    );
  }

  adjustVarianceCount(
    cycleCountId: string,
    itemId: string,
    adjustedActualQty: number,
    adjustedDamagedQty: number | null,
    adjustedBy: string,
    // Optional so callers without a root-cause note (mobile has no note field) need not pass one;
    // a blank value is stored as null server-side. The web review requires it via the modal/UI gate.
    adjustmentNote = ''
  ): Observable<CycleCountVarianceReportItem> {
    return this.http.patch<CycleCountVarianceReportItem>(
      `${BASE_URL}/${cycleCountId}/items/${itemId}/variance-adjustment`,
      {
        adjustedActualQty,
        damagedQty: adjustedDamagedQty,
        adjustedBy,
        adjustmentNote,
      }
    );
  }
}
